import { treeUtil } from '@salescore/buff-common'
import { Array, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'

import {
  equalFilterTypes,
  type FilterType,
  includeFilterTypes,
  startsWithFilterTypes,
} from '../../schemas/query/filterTypes.ts'
import type { ViewConfigFilter, ViewConfigSheet, ViewConfigTreeNode } from '../../schemas/view_config/index.ts'
import type { ModelSearcher } from './compileViewConfig/ModelSearcher.ts'

const RECOMMENDED_MAX_JOIN_OPERATIONS = 5
export const CASE_CONSIDERABLE_FILTER_TYPES = new Set<FilterType>([
  ...equalFilterTypes,
  ...includeFilterTypes,
  ...startsWithFilterTypes,
])

export function hasSheetPerformanceWarnings(sheet: ViewConfigSheet, modelSearcher: ModelSearcher): boolean {
  const { tree, filterTree } = sheet

  const hasJoinWarning = tree !== undefined && hasExcessiveJoinOperationsWarning(tree)
  const hasFilterWarning =
    filterTree !== undefined &&
    pipe(
      filterTree,
      treeUtil.flatNodes,
      Array.flatMap((node) => node.leafs),
      Array.some(
        (leaf) =>
          hasCaseInsensitiveFilterWarning(leaf, modelSearcher) || hasWildcardSearchFilterWarning(leaf, modelSearcher),
      ),
    )

  return hasJoinWarning || hasFilterWarning
}

/**
 * JOINの数が多すぎるかどうかを判定する
 */
export function hasExcessiveJoinOperationsWarning(tree: ViewConfigTreeNode): boolean {
  const flattenTree = treeUtil.flatNodesWithParents(tree)
  return flattenTree.length > RECOMMENDED_MAX_JOIN_OPERATIONS
}

/**
 * 大文字小文字を区別しない比較を行っているかどうかを判定する
 */
export function hasCaseInsensitiveFilterWarning(filter: ViewConfigFilter, modelSearcher: ModelSearcher): boolean {
  // property以外のフィルタでは判定をしない
  if (filter.type !== 'property') return false

  const {
    filterType,
    property: { modelName, propertyName },
  } = filter
  // XXX: id項目はgetModelPropertyで検索できないが、文字列型なので判定対象とする
  if (propertyName === 'id') return CASE_CONSIDERABLE_FILTER_TYPES.has(filterType)

  const { property } = modelSearcher.searchPropertyWithModel(modelName, propertyName) ?? {}
  if (property?.type !== 'string') return false
  if (filter.option?.shouldConsiderCase === true) return false
  return isNotNullish(property.selectOptions) && property.selectOptions.length > 0
    ? ([...includeFilterTypes, ...startsWithFilterTypes] as string[]).includes(filterType)
    : CASE_CONSIDERABLE_FILTER_TYPES.has(filter.filterType)
}

/**
 * 部分一致検索を行っているかどうかを判定する
 */
export function hasWildcardSearchFilterWarning(filter: ViewConfigFilter, modelSearcher: ModelSearcher): boolean {
  // property以外のフィルタでは判定をしない
  if (filter.type !== 'property') return false

  const wildcardSearchFilterTypes = new Set(['include', 'not_include']) // 部分一致検索のフィルタタイプ
  const { modelName, propertyName } = filter.property
  const { property } = modelSearcher.searchPropertyWithModel(modelName, propertyName) ?? {}
  return (
    // XXX: id項目はmodelSearcherで検索できないが、文字列型なので判定対象とする
    (propertyName === 'id' || property?.type === 'string') && wildcardSearchFilterTypes.has(filter.filterType)
  )
}
